/**
 * Authorization context object, types, and consumer hooks.
 *
 * Split out from `AuthorizationContext.tsx` so that the component module only
 * exports the `AuthorizationProvider` component (keeps React Fast Refresh
 * happy). The provider imports the context object from here; consumers import
 * the hooks from here (or via the feature barrel).
 *
 * NOTE: named `authorizationHooks` (not `authorizationContext`) to avoid a
 * case-insensitive filename collision with `AuthorizationContext.tsx` on
 * Windows/macOS filesystems.
 */
import { createContext, useContext } from 'react'

export interface User {
  id: string
  email?: string
  name?: string
}

export interface Delegation {
  id: string
  grantorUserId: string
  grantorName?: string
  grantorEmail?: string
  granteeUserId: string
  permissions: string[]
  status: string
  startsAt?: string
  endsAt?: string
  createdAt: string
}

export interface AuthorizationContextType {
  // User context
  user: User | null
  userId: string | null
  practitionerId: string | null
  orgId: string | null

  // Authorization
  permissions: string[]
  roles: string[]
  isLoading: boolean

  // Delegation
  activeDelegation: string | null // user_id of person being acted on behalf of
  availableDelegations: Delegation[]

  // Methods
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  hasRole: (role: string) => boolean
  refreshPermissions: () => Promise<void>
  setActiveDelegation: (userId: string | null) => void
}

export const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined)

/**
 * Hook to access authorization context
 */
export function useAuthorization() {
  const context = useContext(AuthorizationContext)
  if (!context) {
    throw new Error('useAuthorization must be used within AuthorizationProvider')
  }
  return context
}

/**
 * Hook for common permission checks
 */
export function usePermissions() {
  const { permissions, hasPermission, hasAnyPermission, hasAllPermissions } = useAuthorization()

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Convenience checks for common permissions
    canCreateOrders: hasPermission('order:create'),
    canViewOrders: hasPermission('order:view'),
    canSubmitOrders: hasPermission('order:submit'),
    canViewResults: hasPermission('result:view'),
    canDownloadResults: hasPermission('result:download_pdf'),
    canGrantDelegations: hasPermission('delegate:grant'),
    canInviteUsers: hasPermission('user:invite'),
  }
}
